import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from "@angular/core";
import { CommonModule } from "@angular/common";
import { NbButtonModule, NbDialogService } from "@nebular/theme";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faFileLines } from "@fortawesome/pro-light-svg-icons";
import { Subscription } from "rxjs";
import { filter, skip, switchMap } from "rxjs/operators";
import { DocumentsService } from "../../../@core/utils";
import { DocumentSheetComponent } from "../document-sheet/document-sheet.component";
import { AgGridDocumentsComponent } from "../ag-grid-documents/ag-grid-documents.component";
import { AddDocumentDialogComponent } from "../ag-grid-documents/add-document-dialog/add-document-dialog.component";
import { BasicDialogComponent } from "../basic-dialog/basic-dialog.component";
import { RightPanelDialogService } from "../../../@core/utils/right-panel-dialog.service";

export interface DocumentListItem {
  id: number;
  title: string;
  type: string;
  raw: any;
}

export interface DocumentGroup {
  type: string;
  documents: DocumentListItem[];
}

@Component({
  selector: "app-entity-documents-tab",
  templateUrl: "./entity-documents-tab.component.html",
  styleUrls: ["./entity-documents-tab.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    NbButtonModule,
    TranslateModule,
    FontAwesomeModule,
  ],
})
export class EntityDocumentsTabComponent implements OnChanges, OnDestroy {
  @Input() public investIds: number[] = [];
  @Input() public assetIds: number[] = [];
  @Input() public actionIds: number[] = [];
  @Input() public perimeterIds: number[] = [];
  @Input() public entityLabel: string = '';
  @Output() public documentsChanged = new EventEmitter<void>();

  public faFileLines = faFileLines;
  public documentGroups: DocumentGroup[] = [];
  public loading = true;
  public showAttachMenu = false;

  private subscriptions: Subscription[] = [];

  constructor(
    private cdr: ChangeDetectorRef,
    private dialogService: NbDialogService,
    private documentsService: DocumentsService,
    private translate: TranslateService,
    private sideFormLevelService: RightPanelDialogService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["investIds"] || changes["assetIds"] || changes["actionIds"]) {
      this.loadDocuments();
      this.subscriptions.push(
        this.documentsService._documentsHaveChanged$.pipe(
          skip(1),
        ).subscribe(() => {
          this.loadDocuments();
          this.documentsChanged.emit();
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  private loadDocuments(): void {
    this.loading = true;
    this.subscriptions.push(
      this.documentsService.getDocuments(
        this.assetIds,
        this.investIds,
        this.actionIds,
        [],
      ).subscribe((docArrays) => {
        const allDocs = (docArrays || []).flat();
        const items: DocumentListItem[] = allDocs.map((doc: any) => ({
          id: doc.id,
          title: doc.title || doc.label || "-",
          type: doc.type_object?.name || doc.type || "-",
          raw: doc,
        }));
        const groupMap = new Map<string, DocumentListItem[]>();
        for (const item of items) {
          if (!groupMap.has(item.type)) {
            groupMap.set(item.type, []);
          }
          groupMap.get(item.type)!.push(item);
        }
        this.documentGroups = Array.from(groupMap.entries()).map(([type, documents]) => ({ type, documents }));
        this.loading = false;
        this.cdr.detectChanges();
      })
    );
  }

  public onOpenDocumentSheet(doc: DocumentListItem): void {
    const dialogRef = this.sideFormLevelService.open(DocumentSheetComponent, {
      autoFocus: false,
      closeOnBackdropClick: false,
      hasBackdrop: true,
      context: { doc: doc.raw },
    });
    dialogRef.onClose.subscribe(() => {
      this.documentsService.clearDocumentsCache();
      this.loadDocuments();
    });
  }

  public onDownloadDocument(doc: DocumentListItem): void {
    if (doc.raw?.link) {
      window.open(doc.raw.link, "_blank", "noopener");
    }
  }

  public onDeleteDocument(event: Event, doc: DocumentListItem): void {
    event.stopPropagation();
    const dialog = this.dialogService.open(BasicDialogComponent, {
      autoFocus: false,
      context: {
        title: this.translate.instant("Document deletion"),
        content: this.translate.instant("Do you confirm that {elementName} will be deleted ?", { elementName: doc.title }),
      },
    });
    dialog.onClose.pipe(
      filter((confirmed) => !!confirmed),
      switchMap(() => {
        const raw = doc.raw;
        if (this.investIds.length) {
          const investId = this.investIds[0];
          const remaining: number[] = (raw.linked_investment || []).filter((id: number) => id !== investId);
          if (remaining.length === 0) return this.documentsService.deleteDocument(doc.id);
          return this.documentsService.updateDocumentLinkedInvestment(doc.id, remaining);
        }
        if (this.actionIds.length) {
          const initiativeId = this.actionIds[0];
          const remaining: number[] = (raw.linked_initiative || []).filter((id: number) => id !== initiativeId);
          if (remaining.length === 0) return this.documentsService.deleteDocument(doc.id);
          return this.documentsService.updateDocumentLinkedInitiative(doc.id, remaining);
        }
        if (this.assetIds.length) {
          const assetId = this.assetIds[0];
          const remaining: number[] = (raw.linked_equipment || []).filter((id: number) => id !== assetId);
          if (remaining.length === 0) return this.documentsService.deleteDocument(doc.id);
          return this.documentsService.updateDocumentLinkedEquipment(doc.id, remaining);
        }
        return this.documentsService.deleteDocument(doc.id);
      }),
    ).subscribe({
      next: () => {
        this.documentsService.clearDocumentsCache();
        this.documentsService.documentsHaveChanged();
      },
    });
  }

  public onAttachExistingDocument(): void {
    this.documentsService.clearDocumentsCache();
    const linkedTypeFilter = this.investIds.length ? "investment"
      : this.assetIds.length ? "asset"
      : this.actionIds.length ? "initiative"
      : null;
    const dialogRef = this.sideFormLevelService.open(AgGridDocumentsComponent, {
      autoFocus: false,
      closeOnBackdropClick: false,
      hasBackdrop: true,
      dialogClass: "attach-document-drawer-overlay",
      context: {
        selectionMode: true,
        linkedTypeFilter,
        loadAll: true,
        perimeterIds: [],
        investIds: [],
        assetIds: [],
        actionIds: [],
      },
    });
    dialogRef.onClose.pipe(
      filter((selectedDoc) => !!selectedDoc),
    ).subscribe((selectedDoc: any) => {
      if (this.assetIds.length) {
        const existingLinked: number[] = selectedDoc.linked_equipment || [];
        const assetId = this.assetIds[0];
        if (existingLinked.includes(assetId)) return;
        this.documentsService.updateDocumentLinkedEquipment(
          selectedDoc.id,
          [...existingLinked, assetId]
        ).subscribe({
          next: () => {
            this.documentsService.clearDocumentsCache();
            this.documentsService.documentsHaveChanged();
          },
        });
        return;
      }

      let appLabel: string;
      let model: string;
      let objectId: number;
      if (this.investIds.length) {
        appLabel = "investments";
        model = "investment";
        objectId = this.investIds[0];
      } else if (this.actionIds.length) {
        appLabel = "initiatives";
        model = "initiative";
        objectId = this.actionIds[0];
      } else {
        return;
      }
      this.documentsService.linkDocumentToEntity(
        selectedDoc.id, appLabel, model, objectId
      ).subscribe({
        next: () => {
          this.documentsService.clearDocumentsCache();
          this.documentsService.documentsHaveChanged();
        },
      });
    });
  }

  public onAttachNewDocument(): void {
    this.documentsService.childDialogOpen = true;
    const ref = this.sideFormLevelService.open(AddDocumentDialogComponent, {
      autoFocus: false,
      closeOnBackdropClick: false,
      hasBackdrop: true,
      dialogClass: "add-document-panel-overlay",
      context: {
        investIds: this.investIds,
        assetIds: this.assetIds,
        actionIds: this.actionIds,
        perimeterIds: this.perimeterIds,
        perimeterLabel: "",
        linkedToLabel: this.entityLabel,
        siteId: null,
        initialLabel: "",
      },
    });
    ref.onClose.subscribe(() => {
      this.documentsService.childDialogOpen = false;
    });
  }
}
